import React from 'react';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import moment from 'moment';
import withChartExport from '../../../components/ChartExporting';

const highchartUtils = require('../../../util/highcharts')(Highcharts);
import { getBreakdownData } from '../ThroughputActions';
import { saveConfig, updateBreakdown, updateBreakdownType } from '../../../components/Filter/FilterActions';

import barIcon from '../../../assets/img/bar-icon.svg';
import pieIcon from '../../../assets/img/pie-icon.svg';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';

const pieConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    spacing: [0, 0, 0, 0]
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false,
  },
  legend: {
    enabled: true,
    layout: 'vertical',        
    align: 'left',            
    verticalAlign: 'top',
    itemMarginTop: 4,    
    itemMarginBottom: 4,
    itemStyle: {
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.87)',
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'category'
  },
  plotOptions: {
    pie: {
      innerSize: 0,
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: false,
        useHTML: true,
        formatter: function () {},
        style: {
          fontWeight: 'normal',
          fontSize: '12px',
        }
      }
    }
  },
  series: []
};

const barConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'bar'
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    itemMarginTop: 2,
    itemMarginBottom: 2,
    itemStyle: {
      fontWeight: 'normal'
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  yAxis: {
    title: {
      text: ''
    },
    labels: {
      format: '{value}%'
    },
    reversedStacks: false
  },
  plotOptions: {
    bar: {
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: true
      }
    },
    series: {
      stacking: 'percent'
    }
  },
  series: []
};

const DEFAULT_POS = { x: 95, y: 85 };

export class ThroughputBreakdown extends React.Component {
  static defaultProps = {
    data: []
  };

  constructor(props) {
    super(props);

    this.state = {
      isPieChart: true,
      isFullSreen: false,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    barConfig.exporting = { ...barConfig.exporting, ...this.props.chartExporting };
    pieConfig.exporting = { ...pieConfig.exporting, ...this.props.chartExporting };

    this.initChart();
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });

    if (this.props.data.length) {
      this.props.getBreakdownData(this.props.data);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.props.getBreakdownData(this.props.data);
    }

    if (prevProps.breakdownData != this.props.breakdownData) {
      this.updateChartData();
    }

    if (prevProps.breakdownType != this.props.breakdownType) {
      this.updateChartType();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChartType() {
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.chart.destroy();

    this.initChart();
    this.updateFontSize();
    this.updateChartData();
  }

  initChart() {
    const config = this.props.breakdownType === 'pie' ? pieConfig : barConfig;
    const that = this;
    config.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
    config.plotOptions[this.props.breakdownType].dataLabels.formatter =
      this.props.breakdownType === 'pie'
        ? function() {
            return that.tooltipFormatter(that, this);
          }
        : function() {
            return that.dataLabel(that, this);
          };

    this.chart = new Highcharts['Chart'](`throughput_breakdown_chart_container`, config);
  }

  updateChartData() {
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (this.state.isPieChart) {
      this.chart.addSeries(
        {
          legendType: 'point',
          showInLegend: true,
          colorByPoint: true,
          data: [...this.props.breakdownData]
        },
        true
      );
    } else {
      for (let i = 0; i < this.props.breakdownData.length; i++) {
        const data = {
          ...this.props.breakdownData[i],
          data: [this.props.breakdownData[i].y]
        };
        this.chart.addSeries(data, false);
      }
      this.chart.redraw();
    }
  }

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          legend: {
            itemStyle: {
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '7px',
              fontFamily: 'Poppins'
            }
          },
          plotOptions: {
            pie: {
              dataLabels: {
                style: {
                  color: 'rgba(0, 0, 0, 0.87)',
                  fontSize: '6px',
                  whiteSpace: 'normal',
                  fontFamily: 'Poppins'
                }
              }
            },
          }
        }
      },
      plotOptions: {
        bar: {
          dataLabels: {
            style: {
              fontSize: this.state.isFullSreen ? '16px' : '11px',
              fontWeight: 'bold',
              color: 'contrast'
            }
          }
        },
        pie: {
          dataLabels: {
            enabled: this.state.isFullSreen,
            style: {
              fontSize: this.state.isFullSreen ? '16px' : '11px'
            }
          }
        }
      }
    });
    this.chart.reflow();
  };

  handleSwitch = () => {
    this.setState({ isPieChart: !this.state.isPieChart }, () => {
      this.props.setChartType(this.state.isPieChart ? 'pie' : 'bar');
      this.props.saveConfig();
    });
  };

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          isFullSreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  tooltipFormatter(comp, ev) {
    const name = comp.props.breakdownType === 'pie' ? ev.key : ev.series.name;
    return `<div class="tooltip-label-item"><span class="label-box" style="background-color: ${ev.color}";></span> ${name}: <br/> (${ev.y} ${ev.y === 1 ? 'item' : 'items'}) ${ev.percentage.toFixed(2)}%</div>`;
  }

  dataLabel(comp, ev) {
    return `${ev.percentage.toFixed(2)}%`;
  }

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box breakdown ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className='box_white_title'>
            <h3>Throughput Breakdown</h3>
            <div className='right-actions'>
              <Button variant='secondary' size='sm' className="btn-action" onClick={this.handleSwitch}>
                <img src={this.state.isPieChart ? barIcon : pieIcon} alt="type" />
              </Button>
              <Button variant='secondary' size='sm'
                className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          <div id="throughput_breakdown_chart_container" className='no-stroke' />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getBreakdownData: data => dispatch(getBreakdownData(data)),
    close: () => dispatch(updateBreakdown(false, 'trc')),
    setChartType: type => dispatch(updateBreakdownType(type, 'trc')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    breakdownData: state.throughput.breakdown,
    breakdownType: state.filters.trc.breakdownType
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ThroughputBreakdown));
